import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { passwordSchema } from '@brimostech/shared';
import { Button, Input, Label, Spinner, toast } from '@brimostech/ui';
import { AuthLayout } from '@/features/auth/login-page';
import { api } from '@/lib/api-client';
import { errorMessage } from '@/lib/api-errors';

const schema = z
  .object({
    identifier: z.string().trim().optional(),
    code: z.string().trim().optional(),
    password: passwordSchema,
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'As senhas não conferem',
    path: ['confirm'],
  });
type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      identifier: params.get('identifier') ?? '',
      code: '',
      password: '',
      confirm: '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    if (!token) {
      if (!values.identifier)
        form.setError('identifier', { message: 'Informe seu e-mail ou celular' });
      if (!/^\d{6}$/.test(values.code ?? ''))
        form.setError('code', { message: 'Código de 6 dígitos' });
      if (!values.identifier || !/^\d{6}$/.test(values.code ?? '')) return;
    }
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: token
          ? { token, password: values.password }
          : { code: values.code, identifier: values.identifier, password: values.password },
        auth: false,
      });
      toast.success('Senha redefinida. Entre com a nova senha.');
      navigate('/login', { replace: true });
    } catch (error) {
      setServerError(errorMessage(error));
    }
  });

  const { errors, isSubmitting } = form.formState;
  return (
    <AuthLayout>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Nova senha</h1>
        <p className="text-sm text-muted-foreground">
          {token
            ? 'Escolha a sua nova senha.'
            : 'Informe o código recebido e escolha a nova senha.'}
        </p>
      </div>
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        {!token ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="identifier">E-mail ou celular</Label>
              <Input
                id="identifier"
                autoComplete="username"
                aria-invalid={!!errors.identifier}
                {...form.register('identifier')}
              />
              {errors.identifier ? (
                <p className="text-sm text-destructive">{errors.identifier.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Código de 6 dígitos</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="text-center text-lg tracking-[0.4em]"
                aria-invalid={!!errors.code}
                {...form.register('code')}
              />
              {errors.code ? (
                <p className="text-sm text-destructive">{errors.code.message}</p>
              ) : null}
            </div>
          </>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="password">Nova senha</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.password}
            {...form.register('password')}
          />
          {errors.password ? (
            <p className="text-sm text-destructive">{errors.password.message}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Pelo menos 8 caracteres, com letras e números.
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirme a nova senha</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.confirm}
            {...form.register('confirm')}
          />
          {errors.confirm ? (
            <p className="text-sm text-destructive">{errors.confirm.message}</p>
          ) : null}
        </div>
        {serverError ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {serverError}
          </div>
        ) : null}
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? <Spinner /> : null}
          Salvar nova senha
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          <Link to="/esqueci-senha" className="underline-offset-4 hover:underline">
            Pedir um novo código
          </Link>{' '}
          ·{' '}
          <Link to="/login" className="underline-offset-4 hover:underline">
            Voltar para o login
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
